import { initOAuthManager } from '../utils/oauthManager.js';
import { USER_WORKER_URL } from '../config.js';
import { storage } from '../utils/storage.js';
import { tokenManager } from '../utils/TokenManager.js';

export class UsernameService {
  constructor() {
    this.workerUrl = USER_WORKER_URL;
    this.oauthManager = initOAuthManager();
  }

  async getAuthToken() {
    const token = await this.oauthManager.getValidToken();
    if (!token) throw new Error('Authentication required');
    return token;
  }

  async makeWorkerRequest(endpoint, method = 'GET', body = null) {
    try {
      const options = { method };
      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }

      const response = await tokenManager.authenticatedFetch(`${this.workerUrl}${endpoint}`, options);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Request failed with status ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`[UsernameService] Worker request failed for ${endpoint}:`, error);
      throw error;
    }
  }

  async generateUsername(userData) {
    try {
      console.log('[UsernameService] Generating automatic username for user:', userData.id);

      const result = await this.makeWorkerRequest('/generate-username', 'POST', {});

      return {
        ...result,
        displayUsername: result.displayUsername ?? result.username
      };
    } catch (error) {
      console.error('[UsernameService] Error generating username:', error);
      throw error;
    }
  }

  async changeUsername(newUsername) {
    try {
      console.log('[UsernameService] Changing username to:', newUsername);

      const result = await this.makeWorkerRequest('/change-username', 'POST', {
        new_username: newUsername
      });

      return {
        ...result,
        displayUsername: result.displayUsername ?? result.username
      };
    } catch (error) {
      console.error('[UsernameService] Failed to change username:', error);
      throw error;
    }
  }

  async getUsernameInfo() {
    try {
      return await this.makeWorkerRequest('/username-info', 'GET');
    } catch (error) {
      console.error('[UsernameService] Failed to get username info:', error);
      throw error;
    }
  }

  async checkUsernameAvailability(username) {
    try {
      return await this.makeWorkerRequest('/check-username', 'POST', { username });
    } catch (error) {
      console.error('[UsernameService] Failed to check username availability:', error);
      throw error;
    }
  }

  async assignUsernameOnSignup(userData) {
    return this.generateUsername(userData);
  }

  async regenerateUsername() {
    const userData = storage.get('userData') || {};
    const displayName = userData.name || 'User';

    return this.generateUsername({
      id: userData.id,
      email: userData.email,
      user_metadata: { full_name: displayName }
    });
  }

  async getCurrentUsername() {
    return this.getUsernameInfo();
  }
}

let usernameServiceInstance = null;

export const createUsernameService = () => {
  if (!usernameServiceInstance) {
    usernameServiceInstance = new UsernameService();
  }
  return usernameServiceInstance;
};

export default UsernameService;
